import { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Plus, Eye, FileText, Trash2, CheckCircle } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { WorkOrderWithLines, WorkOrder, Location } from '../types';
import CommentsSection from './CommentsSection';
import Modal from './Modal';
import StockChangeModal from './StockChangeModal';
import { SkeletonTable } from './Skeleton';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import SearchableSelect from './SearchableSelect';

// Función helper para obtener el color del stock según el mínimo
const getStockColor = (stock: number, minStock: number): string => {
  if (stock < minStock) {
    return 'var(--error)'; // Rojo: por debajo del mínimo
  } else if (stock <= minStock * 1.2) {
    return 'var(--warning)'; // Amarillo: igual o hasta 20% por encima del mínimo
  } else {
    return 'var(--success)'; // Verde: más de 20% por encima del mínimo
  }
};

// Función helper para verificar si mostrar icono de alerta (stock < 50% del mínimo)
const shouldShowAlert = (stock: number, minStock: number): boolean => {
  return stock < minStock * 0.5;
};

interface Need {
  metal_piece_id: number;
  cod_pieza: string;
  nombre_pieza: string;
  material_base: 'PLATA' | 'LATON';
  location_id: number;
  location_name: string;
  current_stock: number; // Stock en taller (origen)
  min_stock: number;
  pending_of: number;
  needed: number;
  stock_destino_plata?: number; // Stock en ACABADO_PLATA (destino para PLATA)
  stock_destino_pvd?: number; // Stock en LISTO_PVD (destino para LATON)
  stock_destino_dlc?: number; // Stock en LISTO_DLC (destino para LATON)
  stock_destino_acabado_pvd?: number; // Stock en ACABADO_PVD (destino final para LATON)
  stock_destino_acabado_dlc?: number; // Stock en ACABADO_DLC (destino final para LATON)
}

interface MetalPieceWithStock {
  id: number;
  cod_pieza: string;
  nombre_pieza: string;
  material_base: 'PLATA' | 'LATON';
  current_stock: number;
  min_stock: number;
  needed?: number;
  pending_of?: number;
  stock_destino_plata?: number;
  stock_destino_pvd?: number;
  stock_destino_dlc?: number;
  stock_destino_acabado_pvd?: number;
  stock_destino_acabado_dlc?: number;
}

export default function WorkOrders() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [workOrders, setWorkOrders] = useState<WorkOrderWithLines[]>([]);
  const [needs, setNeeds] = useState<Need[]>([]);
  const [allMetalPieces, setAllMetalPieces] = useState<MetalPieceWithStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isStockChangeModalOpen, setIsStockChangeModalOpen] = useState(false);
  const [stockChanges, setStockChanges] = useState<Array<{ item_code: string; item_name: string; location_name: string; stock_before: number; stock_after: number; quantity_changed: number }>>([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrderWithLines | null>(null);
  const [selectedLines, setSelectedLines] = useState<Map<number, number>>(new Map());
  const [lineQtyInputs, setLineQtyInputs] = useState<Map<number, string>>(new Map());
  const [detailSelectedLines, setDetailSelectedLines] = useState<Set<number>>(new Set());
  const [allAvailableMetalPieces, setAllAvailableMetalPieces] = useState<MetalPieceWithStock[]>([]);
  const [manuallyAddedPieces, setManuallyAddedPieces] = useState<Set<number>>(new Set());
  const [locations, setLocations] = useState<Location[]>([]);
  const [isCompletingAll, setIsCompletingAll] = useState(false);
  const [isCompletingSelected, setIsCompletingSelected] = useState(false);
  const [selectedPieceTypes, setSelectedPieceTypes] = useState<Set<string>>(new Set(['ANILLO', 'COLGANTE', 'PULSERA', 'REASA']));

  // Función helper para determinar el tipo de pieza basándose en el código
  const getPieceType = (codigo: string): 'ANILLO' | 'COLGANTE' | 'PULSERA' | 'REASA' | null => {
    if (!codigo || typeof codigo !== 'string') return null;

    const cleaned = codigo.trim().toUpperCase();
    if (cleaned.length === 0) return null;

    const firstChar = cleaned.charAt(0);

    if (firstChar === 'A') return 'ANILLO';
    if (firstChar === 'C') return 'COLGANTE';
    if (firstChar === 'P') return 'PULSERA';
    if (firstChar === 'R') return 'REASA';

    return null;
  };

  const togglePieceType = (type: string) => {
    setSelectedPieceTypes((prev) => {
      const newTypes = new Set(prev);
      if (newTypes.has(type)) {
        newTypes.delete(type);
      } else {
        newTypes.add(type);
      }
      return newTypes;
    });
  };

  // Filtrar piezas: mostrar solo las que tienen necesidad O las agregadas manualmente
  // PERO excluir las que no tienen stock en taller (no se pueden fabricar sin material base)
  // Y filtrar por tipo de pieza seleccionado
  const visibleMetalPieces = useMemo(() => {
    return allMetalPieces.filter(piece => {
      // No mostrar piezas sin stock en taller (no se pueden fabricar)
      if ((piece.current_stock || 0) <= 0) {
        return false;
      }
      const hasNeed = piece.needed !== undefined && piece.needed > 0;
      const isManuallyAdded = manuallyAddedPieces.has(piece.id);
      if (!hasNeed && !isManuallyAdded) return false;

      // Filtrar por tipo de pieza
      const pieceType = getPieceType(piece.cod_pieza);
      if (pieceType && !selectedPieceTypes.has(pieceType)) return false;

      return true;
    });
  }, [allMetalPieces, manuallyAddedPieces, selectedPieceTypes]);

  // Filtrar piezas disponibles para agregar manualmente: solo las que tienen stock en taller
  // Y filtrar por tipo de pieza seleccionado
  const availableMetalPiecesForManualAdd = useMemo(() => {
    return allAvailableMetalPieces.filter(piece => {
      if ((piece.current_stock || 0) <= 0) return false;

      // Filtrar por tipo de pieza
      const pieceType = getPieceType(piece.cod_pieza);
      if (pieceType && !selectedPieceTypes.has(pieceType)) return false;

      return true;
    });
  }, [allAvailableMetalPieces, selectedPieceTypes]);

  const selectablePiecesCount = useMemo(
    () => visibleMetalPieces.filter(piece => (piece.current_stock || 0) > 0).length,
    [visibleMetalPieces]
  );

  // Paginación para workOrders
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    paginatedItems: paginatedWorkOrders,
    setCurrentPage,
    setItemsPerPage,
  } = usePagination(workOrders, {
    initialPage: 1,
    initialItemsPerPage: 25,
  });

  useEffect(() => {
    loadInitialData();
  }, []);


  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Usar endpoint combinado para cargar todos los datos en una sola llamada
      const response = await api.get('/work-orders/initial-data');
      setWorkOrders(response.data.workOrders);
      setNeeds(response.data.needs);

      // Asegurar que metalPieces sea un array (puede venir como objeto con data si es paginado)
      const metalPiecesArray = Array.isArray(response.data.metalPieces)
        ? response.data.metalPieces
        : (response.data.metalPieces?.data || []);

      const stockArray = Array.isArray(response.data.stock) ? response.data.stock : [];
      const minStockArray = Array.isArray(response.data.minStock) ? response.data.minStock : [];
      const locationsArray = Array.isArray(response.data.locations) ? response.data.locations : [];

      // Cargar piezas con stock usando los datos del endpoint combinado
      await loadAllMetalPiecesWithData(
        response.data.needs || [],
        metalPiecesArray,
        stockArray,
        minStockArray,
        locationsArray
      );
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar datos iniciales');
      // Fallback a carga individual si el endpoint combinado falla
      loadWorkOrders();
      loadNeeds();
    } finally {
      setLoading(false);
    }
  };

  const loadWorkOrders = async () => {
    try {
      const response = await api.get('/work-orders');
      setWorkOrders(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar órdenes de fabricación');
    }
  };

  const loadNeeds = async (): Promise<Need[]> => {
    try {
      const response = await api.get('/work-orders/needs');
      setNeeds(response.data);
      return response.data;
    } catch (err: any) {
      showError('Error al cargar necesidades');
      return [];
    }
  };

  const loadAllMetalPieces = async (overrideNeeds?: Need[]) => {
    try {
      // Cargar todas las piezas metálicas (fallback si no se usó endpoint combinado)
      const [piecesRes, stockRes, minStockRes, locationsRes] = await Promise.all([
        api.get('/metal-pieces'),
        api.get('/stock?item_type=METAL'),
        api.get('/min-stock?item_type=METAL'),
        api.get('/locations'),
      ]);

      await loadAllMetalPiecesWithData(
        overrideNeeds || needs,
        piecesRes.data,
        stockRes.data,
        minStockRes.data,
        locationsRes.data
      );
    } catch (err: any) {
      showError('Error al cargar piezas metálicas');
    }
  };

  const loadAllMetalPiecesWithData = async (
    overrideNeeds: Need[],
    piecesData: any[],
    stockData: any[],
    minStockData: any[],
    locationsData: any[]
  ) => {
    try {
      // Validar que los datos sean arrays
      if (!Array.isArray(piecesData) || !Array.isArray(stockData) || !Array.isArray(minStockData) || !Array.isArray(locationsData)) {
        showError('Error: Los datos recibidos no tienen el formato esperado');
        return;
      }

      const pieces = piecesData.filter((p: any) => p && p.is_active !== false);

      const stock = stockData;
      const minStock = minStockData;
      const locations = locationsData;

      // Mapear ubicaciones
      const tallerPlata = locations.find((l: any) => l.code === 'TALLER_PLATA');
      const tallerLaton = locations.find((l: any) => l.code === 'TALLER_LATON');
      const acabadoPlata = locations.find((l: any) => l.code === 'ACABADO_PLATA');
      const listoPVD = locations.find((l: any) => l.code === 'LISTO_PVD');
      const listoDLC = locations.find((l: any) => l.code === 'LISTO_DLC');
      const acabadoPVD = locations.find((l: any) => l.code === 'ACABADO_PVD');
      const acabadoDLC = locations.find((l: any) => l.code === 'ACABADO_DLC');

      if (!tallerPlata && !tallerLaton) {
        showError('Error: No se encontraron ubicaciones de taller necesarias');
      }

      // Crear un mapa de necesidades por pieza
      const effectiveNeeds = overrideNeeds ?? needs;
      const needsMap = new Map<number, Need>();
      effectiveNeeds.forEach((need) => {
        needsMap.set(need.metal_piece_id, need);
      });

      // Combinar piezas con stock y necesidades
      const piecesWithStock: MetalPieceWithStock[] = pieces.map((piece: any) => {
        const need = needsMap.get(piece.id);
        const tallerLocationId = piece.material_base === 'PLATA' ? tallerPlata?.id : tallerLaton?.id;
        const currentStock = stock.find((s: any) =>
          s.item_type === 'METAL' &&
          s.item_id === piece.id &&
          s.location_id === tallerLocationId
        )?.qty || 0;

        const minStockForPiece = minStock.find((ms: any) =>
          ms.item_type === 'METAL' &&
          ms.item_id === piece.id &&
          ms.location_id === tallerLocationId
        )?.min_qty_base || 0;

        // Obtener stock en ubicaciones de destino
        const stockDestinoPlata = piece.material_base === 'PLATA'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === acabadoPlata?.id
          )?.qty || 0
          : undefined;

        const stockDestinoPVD = piece.material_base === 'LATON'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === listoPVD?.id
          )?.qty || 0
          : undefined;

        const stockDestinoDLC = piece.material_base === 'LATON'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === listoDLC?.id
          )?.qty || 0
          : undefined;

        const stockDestinoAcabadoPVD = piece.material_base === 'LATON'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === acabadoPVD?.id
          )?.qty || 0
          : undefined;

        const stockDestinoAcabadoDLC = piece.material_base === 'LATON'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === acabadoDLC?.id
          )?.qty || 0
          : undefined;

        return {
          id: piece.id,
          cod_pieza: piece.cod_pieza,
          nombre_pieza: piece.nombre_pieza,
          material_base: piece.material_base,
          current_stock: currentStock,
          min_stock: minStockForPiece,
          needed: need?.needed,
          pending_of: need?.pending_of,
          stock_destino_plata: stockDestinoPlata,
          stock_destino_pvd: stockDestinoPVD,
          stock_destino_dlc: stockDestinoDLC,
          stock_destino_acabado_pvd: stockDestinoAcabadoPVD,
          stock_destino_acabado_dlc: stockDestinoAcabadoDLC,
        };
      });

      setAllMetalPieces(piecesWithStock);
    } catch (err: any) {
      showError('Error al cargar piezas metálicas: ' + (err.message || 'Error desconocido'));
      setAllMetalPieces([]);
    }
  };

  const handleOpenCreateModal = async () => {
    setSelectedLines(new Map());
    setManuallyAddedPieces(new Set());
    try {
      // Cargar datos frescos usando el endpoint combinado (forzar recarga sin caché)
      const response = await api.get('/work-orders/initial-data', {
        params: { _t: Date.now() } // Timestamp para evitar caché
      });

      // Validar que la respuesta tenga los datos necesarios
      if (!response.data) {
        throw new Error('Respuesta vacía del servidor');
      }

      const freshNeeds = Array.isArray(response.data.needs) ? response.data.needs : [];
      setNeeds(freshNeeds);

      // Asegurar que metalPieces sea un array (puede venir como objeto con data si es paginado)
      const metalPiecesArray = Array.isArray(response.data.metalPieces)
        ? response.data.metalPieces
        : (response.data.metalPieces?.data || []);

      const stockArray = Array.isArray(response.data.stock) ? response.data.stock : [];
      const minStockArray = Array.isArray(response.data.minStock) ? response.data.minStock : [];
      const locationsArray = Array.isArray(response.data.locations) ? response.data.locations : [];

      // Cargar piezas con stock usando los datos del endpoint combinado
      await loadAllMetalPiecesWithData(
        freshNeeds,
        metalPiecesArray,
        stockArray,
        minStockArray,
        locationsArray
      );

      // Cargar todas las piezas disponibles para la búsqueda manual
      await loadAllAvailableMetalPieces(metalPiecesArray, stockArray, minStockArray, locationsArray);

      // Esperar un momento para que React actualice el estado
      await new Promise(resolve => setTimeout(resolve, 100));

      setIsCreateModalOpen(true);
    } catch (err: any) {
      showError('Error al cargar datos necesarios para crear la orden');
      // Fallback: intentar cargar datos individualmente
      try {
        const freshNeeds = await loadNeeds();
        await loadAllMetalPieces(freshNeeds);
        setIsCreateModalOpen(true);
      } catch (fallbackErr: any) {
        showError('No se pudieron cargar los datos necesarios');
      }
    }
  };

  const loadAllAvailableMetalPieces = async (
    piecesData: any[],
    stockData: any[],
    minStockData: any[],
    locationsData: any[]
  ) => {
    try {
      const pieces = piecesData.filter((p: any) => p && p.is_active !== false);
      const stock = stockData;
      const minStock = minStockData;
      const locations = locationsData;

      const tallerPlata = locations.find((l: any) => l.code === 'TALLER_PLATA');
      const tallerLaton = locations.find((l: any) => l.code === 'TALLER_LATON');
      const acabadoPlata = locations.find((l: any) => l.code === 'ACABADO_PLATA');
      const listoPVD = locations.find((l: any) => l.code === 'LISTO_PVD');
      const listoDLC = locations.find((l: any) => l.code === 'LISTO_DLC');
      const acabadoPVD = locations.find((l: any) => l.code === 'ACABADO_PVD');
      const acabadoDLC = locations.find((l: any) => l.code === 'ACABADO_DLC');

      const piecesWithStock: MetalPieceWithStock[] = pieces.map((piece: any) => {
        const tallerLocationId = piece.material_base === 'PLATA' ? tallerPlata?.id : tallerLaton?.id;
        const currentStock = stock.find((s: any) =>
          s.item_type === 'METAL' &&
          s.item_id === piece.id &&
          s.location_id === tallerLocationId
        )?.qty || 0;

        const minStockForPiece = minStock.find((ms: any) =>
          ms.item_type === 'METAL' &&
          ms.item_id === piece.id &&
          ms.location_id === tallerLocationId
        )?.min_qty_base || 0;

        const stockDestinoPlata = piece.material_base === 'PLATA'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === acabadoPlata?.id
          )?.qty || 0
          : undefined;

        const stockDestinoPVD = piece.material_base === 'LATON'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === listoPVD?.id
          )?.qty || 0
          : undefined;

        const stockDestinoDLC = piece.material_base === 'LATON'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === listoDLC?.id
          )?.qty || 0
          : undefined;

        const stockDestinoAcabadoPVD = piece.material_base === 'LATON'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === acabadoPVD?.id
          )?.qty || 0
          : undefined;

        const stockDestinoAcabadoDLC = piece.material_base === 'LATON'
          ? stock.find((s: any) =>
            s.item_type === 'METAL' &&
            s.item_id === piece.id &&
            s.location_id === acabadoDLC?.id
          )?.qty || 0
          : undefined;

        return {
          id: piece.id,
          cod_pieza: piece.cod_pieza,
          nombre_pieza: piece.nombre_pieza,
          material_base: piece.material_base,
          current_stock: currentStock,
          min_stock: minStockForPiece,
          needed: undefined, // No tiene necesidad por defecto
          stock_destino_plata: stockDestinoPlata,
          stock_destino_pvd: stockDestinoPVD,
          stock_destino_dlc: stockDestinoDLC,
          stock_destino_acabado_pvd: stockDestinoAcabadoPVD,
          stock_destino_acabado_dlc: stockDestinoAcabadoDLC,
        };
      });

      setAllAvailableMetalPieces(piecesWithStock);
    } catch (err: any) {
      showError('Error al cargar todas las piezas disponibles');
    }
  };

  const handleManualAddPiece = async (piece: MetalPieceWithStock) => {
    // Agregar a la lista de piezas manualmente agregadas
    setManuallyAddedPieces(prev => new Set([...prev, piece.id]));

    // Verificar si la pieza ya está en allMetalPieces con stock correcto
    const existingPiece = allMetalPieces.find(p => p.id === piece.id);

    // Si no está en allMetalPieces o el stock parece incorrecto, consultar stock real del backend
    if (!existingPiece || (piece.current_stock === 0 && piece.min_stock === 0)) {
      try {
        // Cargar ubicaciones si no están cargadas
        let allLocations = locations;
        if (allLocations.length === 0) {
          const locationsResponse = await api.get('/locations');
          allLocations = locationsResponse.data;
          setLocations(allLocations);
        }

        const tallerPlata = allLocations.find((l: Location) => l.code === 'TALLER_PLATA');
        const tallerLaton = allLocations.find((l: Location) => l.code === 'TALLER_LATON');
        const tallerLocationId = piece.material_base === 'PLATA' ? tallerPlata?.id : tallerLaton?.id;

        // Consultar stock real
        const stockResponse = await api.get('/stock', {
          params: {
            item_type: 'METAL',
            item_id: piece.id,
            location_id: tallerLocationId
          }
        });
        const stockData = stockResponse.data;
        const currentStock = Array.isArray(stockData) && stockData.length > 0
          ? parseFloat(stockData[0].qty) || 0
          : (piece.current_stock || 0);

        // Consultar mínimo de stock
        const minStockResponse = await api.get('/min-stock', {
          params: {
            item_type: 'METAL',
            item_id: piece.id,
            location_id: tallerLocationId
          }
        });
        const minStockData = minStockResponse.data;
        const minStockBase = Array.isArray(minStockData) && minStockData.length > 0
          ? parseFloat(minStockData[0].min_qty_base) || 0
          : (piece.min_stock || 0);

        // Obtener multiplicador
        const multiplierResponse = await api.get('/settings/min-stock-multiplier');
        // Bug fix: El backend devuelve { multiplier }, no { value }
        const multiplier = parseFloat(multiplierResponse.data.multiplier || '1');
        const effectiveMin = Math.round(minStockBase * multiplier);

        // Actualizar la pieza con stock real
        const pieceWithRealStock: MetalPieceWithStock = {
          ...piece,
          current_stock: currentStock,
          min_stock: effectiveMin,
        };

        // Si no está en allMetalPieces, agregarla con stock real
        if (!existingPiece) {
          setAllMetalPieces(prev => [...prev, pieceWithRealStock]);
        } else {
          // Actualizar la pieza existente con stock real
          setAllMetalPieces(prev => prev.map(p =>
            p.id === piece.id ? pieceWithRealStock : p
          ));
        }

        // Seleccionar la pieza con cantidad sugerida basada en stock real
        const maxQty = Math.max(0, currentStock);
        const suggested = maxQty > 0 ? Math.min(1, maxQty) : 1;
        const newMap = new Map(selectedLines);
        newMap.set(piece.id, suggested);
        setSelectedLines(newMap);
      } catch (err: any) {
        // Fallback: usar los valores de la pieza recibida
        if (!existingPiece) {
          setAllMetalPieces(prev => [...prev, piece]);
        }
        const maxQty = Math.max(0, piece.current_stock || 0);
        const suggested = maxQty > 0 ? Math.min(1, maxQty) : 1;
        const newMap = new Map(selectedLines);
        newMap.set(piece.id, suggested);
        setSelectedLines(newMap);
      }
    } else {
      // La pieza ya está en allMetalPieces con stock correcto, solo seleccionarla
      const maxQty = Math.max(0, piece.current_stock || 0);
      const suggested = maxQty > 0 ? Math.min(1, maxQty) : 1;
      const newMap = new Map(selectedLines);
      newMap.set(piece.id, suggested);
      setSelectedLines(newMap);
    }

    showSuccess(`${piece.cod_pieza} agregado manualmente`);
  };

  const getPieceById = (id: number): MetalPieceWithStock | undefined => {
    return allMetalPieces.find(piece => piece.id === id) || allAvailableMetalPieces.find(piece => piece.id === id);
  };

  const handleToggleLine = (piece: MetalPieceWithStock) => {
    const newMap = new Map(selectedLines);

    if (newMap.has(piece.id)) {
      newMap.delete(piece.id);
    } else {
      const maxQty = Math.max(0, piece.current_stock || 0);
      if (maxQty <= 0) {
        showError(`No hay stock en taller para ${piece.cod_pieza}`);
        return;
      }
      const suggested = Math.min(piece.needed && piece.needed > 0 ? piece.needed : maxQty, maxQty);
      newMap.set(piece.id, suggested);
    }
    setSelectedLines(newMap);
  };

  const handleUpdateQty = (metalPieceId: number, qty: number) => {
    const piece = getPieceById(metalPieceId);
    const maxQty = Math.max(0, piece?.current_stock || 0);
    const clampedQty = Math.min(Math.max(qty, 0), maxQty);

    const newMap = new Map(selectedLines);
    if (clampedQty > 0) {
      newMap.set(metalPieceId, clampedQty);
    } else {
      newMap.delete(metalPieceId);
    }

    if (qty > maxQty) {
      showError(`Cantidad máxima para ${piece?.cod_pieza || ''} es ${maxQty}`);
    }

    setSelectedLines(newMap);
  };

  const handleCreateWorkOrder = async () => {
    if (selectedLines.size === 0) {
      showError('Debes seleccionar al menos una línea');
      return;
    }

    try {

      const lines = Array.from(selectedLines.entries()).map(([metal_piece_id, qty_planned]) => {
        const pieceInfo = getPieceById(metal_piece_id);
        const maxQty = pieceInfo ? Math.max(0, pieceInfo.current_stock || 0) : qty_planned;
        const adjustedQty = Math.min(qty_planned, maxQty);

        return {
          metal_piece_id,
          qty_planned: adjustedQty,
        };
      }).filter((line) => line.qty_planned > 0);

      if (lines.length === 0) {
        showError('No hay stock disponible en taller para las piezas seleccionadas');
        return;
      }

      await api.post('/work-orders', { lines });
      showSuccess('Orden de fabricación creada correctamente');
      setIsCreateModalOpen(false);
      setSelectedLines(new Map());
      loadWorkOrders();
      loadNeeds();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al crear orden de fabricación');
    }
  };

  const initializeDetailLineState = (workOrderData: WorkOrderWithLines) => {
    const qtyInputs = new Map<number, string>();
    const selectedSet = new Set<number>();
    workOrderData.lines.forEach((line) => {
      const pending = line.qty_planned - line.qty_done;
      if (pending > 0) {
        qtyInputs.set(line.id, pending.toString());
        selectedSet.add(line.id);
      }
    });
    setLineQtyInputs(qtyInputs);
    setDetailSelectedLines(selectedSet);
  };

  const handleOpenDetailModal = async (workOrder: WorkOrderWithLines) => {
    try {
      const response = await api.get(`/work-orders/${workOrder.id}`);
      const fetchedWO = response.data;
      initializeDetailLineState(fetchedWO);
      setSelectedWorkOrder(fetchedWO);
      setIsDetailModalOpen(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar detalles');
    }
  };

  const handleCompleteLine = async (workOrderId: number, lineId: number, qty: number) => {
    if (qty <= 0) {
      showError('La cantidad debe ser mayor a 0');
      return;
    }

    try {
      const response = await api.post(`/work-orders/${workOrderId}/lines/${lineId}/complete`, { qty_done: qty });
      showSuccess('Línea completada correctamente');

      if (response.data.stockChanges && response.data.stockChanges.length > 0) {
        setStockChanges(response.data.stockChanges);
        setIsStockChangeModalOpen(true);
      }

      loadWorkOrders();
      if (selectedWorkOrder) {
        const workOrderResponse = await api.get(`/work-orders/${workOrderId}`);
        const updatedWorkOrder = workOrderResponse.data;
        initializeDetailLineState(updatedWorkOrder);
        setSelectedWorkOrder(updatedWorkOrder);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al completar línea');
    }
  };

  const toggleDetailLineSelection = (lineId: number) => {
    const newSet = new Set(detailSelectedLines);
    if (newSet.has(lineId)) {
      newSet.delete(lineId);
    } else {
      newSet.add(lineId);
    }
    setDetailSelectedLines(newSet);
  };

  const toggleSelectAllDetailLines = () => {
    if (!selectedWorkOrder) return;
    const pendingIds = selectedWorkOrder.lines
      .filter((line) => line.qty_planned > line.qty_done)
      .map((line) => line.id);
    if (pendingIds.length === 0) {
      setDetailSelectedLines(new Set());
      return;
    }
    const allSelected = pendingIds.every((id) => detailSelectedLines.has(id));
    if (allSelected) {
      setDetailSelectedLines(new Set());
      return;
    }
    const newSet = new Set<number>();
    pendingIds.forEach((id) => newSet.add(id));
    setDetailSelectedLines(newSet);
  };

  const handleCompleteSelectedLines = async () => {
    if (!selectedWorkOrder || detailSelectedLines.size === 0 || isCompletingSelected) return;
    const orderId = selectedWorkOrder.id;
    const linesToComplete = [];

    for (const lineId of Array.from(detailSelectedLines)) {
      const line = selectedWorkOrder.lines.find((l) => l.id === lineId);
      if (!line) continue;
      const pending = line.qty_planned - line.qty_done;
      if (pending <= 0) continue;
      const qtyInputValue = lineQtyInputs.get(lineId) || pending.toString();
      const qty = parseInt(qtyInputValue, 10) || pending;
      if (qty <= 0) continue;

      linesToComplete.push({ lineId, qty_done: qty });
    }

    if (linesToComplete.length > 0) {
      try {
        setIsCompletingSelected(true);
        const response = await api.post(`/work-orders/${orderId}/complete-lines`, { lines: linesToComplete });
        showSuccess('Líneas seleccionadas completadas correctamente');

        if (response.data.stockChanges && response.data.stockChanges.length > 0) {
          setStockChanges(response.data.stockChanges);
          setIsStockChangeModalOpen(true);
        }

        loadWorkOrders();

        // Refresh detail
        const workOrderResponse = await api.get(`/work-orders/${orderId}`);
        const updatedWorkOrder = workOrderResponse.data;
        const qtyInputs = new Map(lineQtyInputs);
        updatedWorkOrder.lines.forEach((line: any) => {
          const pending = line.qty_planned - line.qty_done;
          if (pending > 0) {
            qtyInputs.set(line.id, pending.toString());
          } else {
            qtyInputs.delete(line.id);
          }
        });
        setLineQtyInputs(qtyInputs);
        setSelectedWorkOrder(updatedWorkOrder);
        setDetailSelectedLines(new Set());

        // Cerrar el modal después de completar
        setIsDetailModalOpen(false);
        setSelectedWorkOrder(null);
        setDetailSelectedLines(new Set());
      } catch (err: any) {
        showError(err.response?.data?.error || 'Error al completar líneas seleccionadas');
      } finally {
        setIsCompletingSelected(false);
      }
    }
  };

  const handleCompleteAllLines = async (workOrder: WorkOrderWithLines) => {
    if (!workOrder || isCompletingAll) return;

    try {
      setIsCompletingAll(true);
      const linesToComplete = [];
      for (const line of workOrder.lines) {
        const pending = line.qty_planned - line.qty_done;
        if (pending > 0) {
          linesToComplete.push({ lineId: line.id, qty_done: pending });
        }
      }

      if (linesToComplete.length > 0) {
        await api.post(`/work-orders/${workOrder.id}/complete-lines`, { lines: linesToComplete });
        showSuccess('Todas las líneas se completaron correctamente');
        loadWorkOrders();

        // Refresh detail
        const response = await api.get(`/work-orders/${workOrder.id}`);
        const updatedWO = response.data;
        const qtyInputs = new Map();
        updatedWO.lines.forEach((line: any) => {
          const pending = line.qty_planned - line.qty_done;
          if (pending > 0) {
            qtyInputs.set(line.id, pending.toString());
          }
        });
        setLineQtyInputs(qtyInputs);
        setSelectedWorkOrder(updatedWO);

        // Cerrar el modal después de completar todo
        setIsDetailModalOpen(false);
        setSelectedWorkOrder(null);
        setDetailSelectedLines(new Set());
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al completar todas las líneas');
    } finally {
      setIsCompletingAll(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta orden de fabricación?')) {
      return;
    }

    try {
      await api.delete(`/work-orders/${id}`);
      showSuccess('Orden de fabricación eliminada correctamente');
      loadWorkOrders();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Error al eliminar orden de fabricación');
    }
  };

  const handleCloseWorkOrder = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres cerrar manualmente esta orden de fabricación?')) {
      return;
    }

    try {
      await api.post(`/work-orders/${id}/close`);
      showSuccess('Orden de fabricación cerrada correctamente');
      loadWorkOrders();
      loadNeeds();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cerrar orden de fabricación');
    }
  };

  const handleStatusChange = async (workOrderId: number, newStatus: WorkOrderWithLines['status']) => {
    try {
      await api.put(`/work-orders/${workOrderId}/status`, { status: newStatus });
      showSuccess('Estado de orden actualizado correctamente');
      loadWorkOrders();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar estado de orden');
    }
  };

  const handleRecalculateAllPriorities = async () => {
    try {
      const response = await api.post('/work-orders/priorities/recalculate');
      showSuccess(`Prioridades recalculadas: ${response.data.updated} actualizadas`);
      loadWorkOrders();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recalcular prioridades');
    }
  };

  const handleDownloadPDF = async (workOrderId: number, workOrderCode: string) => {
    try {
      const response = await api.get(`/work-orders/${workOrderId}/pdf`, {
        responseType: 'blob',
      });

      // Verificar si la respuesta es un error (JSON en lugar de PDF)
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        showError(errorData.error || 'Error al generar PDF');
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orden-fabricacion-${workOrderCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('PDF generado correctamente');
    } catch (err: any) {
      let errorMessage = 'Error al generar PDF';

      if (err.response?.data) {
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch {
            errorMessage = err.message || errorMessage;
          }
        } else {
          errorMessage = err.response.data.error || errorMessage;
        }
      }

      showError(errorMessage);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'var(--warning)';
      case 'IN_PROGRESS':
        return 'var(--accent)';
      case 'DONE':
        return 'var(--success)';
      case 'CANCELLED':
        return 'var(--error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'IN_PROGRESS':
        return 'En Progreso';
      case 'DONE':
        return 'Completada';
      case 'CANCELLED':
        return 'Cancelada';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <SkeletonTable rows={8} columns={6} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="flex-between mb-2">
        <h2>Órdenes de Fabricación</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {canEdit('work-orders') && (
            <>
              <button
                className="btn btn-secondary"
                onClick={handleRecalculateAllPriorities}
                title="Recalcular todas las prioridades automáticamente"
              >
                <RefreshCw size={16} /> Recalcular Prioridades
              </button>
              <button className="btn btn-primary" onClick={handleOpenCreateModal}>
                <Plus size={16} /> Nueva OF desde Necesidades
              </button>
            </>
          )}
        </div>
      </div>

      <>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Estado</th>
                <th>Líneas</th>
                <th>Fecha Creación</th>
                <th>Fecha Completación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginatedWorkOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No hay órdenes de fabricación
                  </td>
                </tr>
              ) : (
                paginatedWorkOrders.map((wo) => (
                  <tr key={wo.id}>
                    <td>
                      <strong>{wo.code}</strong>
                    </td>
                    <td>
                      <span style={{ color: getStatusColor(wo.status) }}>
                        {getStatusLabel(wo.status)}
                      </span>
                    </td>
                    <td>
                      {wo.lines.length} línea(s)
                      <br />
                      <small style={{ color: 'var(--text-secondary)' }}>
                        {wo.lines.reduce((sum, l) => sum + l.qty_planned, 0)} unidades planificadas
                      </small>
                    </td>
                    <td>{formatDate(wo.created_at)}</td>
                    <td>
                      {wo.completed_at
                        ? formatDate(wo.completed_at)
                        : '-'}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleOpenDetailModal(wo)}
                          title="Ver"
                          style={{
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            transition: 'all 0.2s ease',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                            e.currentTarget.style.color = 'var(--text-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(wo.id, wo.code)}
                          title="Generar PDF"
                          style={{
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            transition: 'all 0.2s ease',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                            e.currentTarget.style.color = 'var(--text-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }}
                        >
                          <FileText size={16} />
                        </button>
                        {canEdit('work-orders') && (wo.status === 'PENDING' || wo.status === 'IN_PROGRESS') && (
                          <button
                            onClick={() => handleCloseWorkOrder(wo.id)}
                            title="Cerrar Manualmente"
                            style={{
                              padding: '0.5rem',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--success)',
                              transition: 'all 0.2s ease',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                              e.currentTarget.style.color = 'var(--success)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = 'var(--success)';
                            }}
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                        {wo.status !== 'DONE' && canEdit('work-orders') && (
                          <button
                            onClick={() => handleDelete(wo.id)}
                            title="Eliminar"
                            style={{
                              padding: '0.5rem',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--text-secondary)',
                              transition: 'all 0.2s ease',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                              e.currentTarget.style.color = 'var(--text-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {workOrders.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={workOrders.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            showItemsPerPage={true}
          />
        )}
      </>

      {/* Modal Crear OF */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nueva Orden de Fabricación desde Necesidades"
        size="large"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setIsCreateModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateWorkOrder}
              disabled={selectedLines.size === 0}
              data-modal-default="true"
            >
              Crear OF
            </button>
          </>
        }
      >
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ color: 'var(--text-secondary)' }}>
            Selecciona las piezas para la orden de fabricación. Solo se muestran piezas que tienen stock disponible en taller. Las cantidades sugeridas se basan en las necesidades calculadas, pero puedes modificarlas manualmente.
          </p>
        </div>

        {/* Filtro por tipo de pieza */}
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
            Filtrar por tipo de pieza:
          </label>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {[
              { value: 'ANILLO', label: 'Anillos', color: '#3b82f6' },
              { value: 'COLGANTE', label: 'Colgantes', color: '#a855f7' },
              { value: 'PULSERA', label: 'Pulseras', color: '#10b981' },
              { value: 'REASA', label: 'Resas', color: '#f59e0b' },
            ].map((type) => (
              <label
                key={type.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  backgroundColor: selectedPieceTypes.has(type.value) ? `${type.color}20` : 'transparent',
                  border: `2px solid ${selectedPieceTypes.has(type.value) ? type.color : 'var(--border-color)'}`,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!selectedPieceTypes.has(type.value)) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedPieceTypes.has(type.value)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPieceTypes.has(type.value)}
                  onChange={() => togglePieceType(type.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{
                  color: selectedPieceTypes.has(type.value) ? type.color : 'var(--text-secondary)',
                  fontWeight: selectedPieceTypes.has(type.value) ? 'bold' : 'normal'
                }}>
                  {type.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <SearchableSelect<MetalPieceWithStock>
            items={availableMetalPiecesForManualAdd}
            onSelect={handleManualAddPiece}
            getItemLabel={(item) => item.nombre_pieza}
            getItemId={(item) => item.id}
            getItemCode={(item) => item.cod_pieza}
            placeholder="Buscar pieza por código o nombre para agregar manualmente..."
            label="Agregar Pieza Manualmente"
            excludeIds={new Set([...selectedLines.keys()])}
          />
        </div>

        {visibleMetalPieces.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            Cargando piezas metálicas...
          </p>
        ) : (
          <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectablePiecesCount > 0 && selectedLines.size === selectablePiecesCount}
                      disabled={selectablePiecesCount === 0}
                      onChange={() => {
                        if (selectablePiecesCount > 0 && selectedLines.size === selectablePiecesCount) {
                          setSelectedLines(new Map());
                        } else {
                          const newMap = new Map<number, number>();
                          visibleMetalPieces.forEach((p) => {
                            const maxQty = Math.max(0, p.current_stock || 0);
                            if (maxQty > 0) {
                              const suggested = Math.min(
                                p.needed && p.needed > 0 ? p.needed : maxQty,
                                maxQty
                              );
                              if (suggested > 0) {
                                newMap.set(p.id, suggested);
                              }
                            }
                          });
                          setSelectedLines(newMap);
                        }
                      }}
                    />
                  </th>
                  <th style={{ minWidth: '180px' }}>Código</th>
                  <th style={{ minWidth: '300px' }}>Nombre</th>
                  <th style={{ minWidth: '120px', textAlign: 'center' }}>Stock Origen<br /><small>(Taller)</small></th>
                  <th style={{ minWidth: '150px', textAlign: 'center' }}>Stock Destino</th>
                  <th style={{ minWidth: '100px', textAlign: 'center' }}>Mínimo</th>
                  <th style={{ minWidth: '100px', textAlign: 'center' }}>Pendiente OF</th>
                  <th style={{ minWidth: '120px', textAlign: 'center' }}>Necesario</th>
                  <th style={{ minWidth: '150px' }}>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {visibleMetalPieces.map((piece) => {
                  const maxQty = Math.max(0, piece.current_stock || 0);
                  const isSelected = selectedLines.has(piece.id);
                  const selectedQty = selectedLines.get(piece.id);
                  const defaultQty =
                    maxQty > 0
                      ? Math.min(piece.needed && piece.needed > 0 ? piece.needed : 1, maxQty)
                      : 0;
                  const qty = selectedQty !== undefined ? Math.min(selectedQty, maxQty) : defaultQty;
                  const hasNeed = piece.needed !== undefined && piece.needed > 0;
                  return (
                    <tr key={piece.id} style={{ opacity: hasNeed ? 1 : 0.7 }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={piece.current_stock <= 0}
                          onChange={() => handleToggleLine(piece)}
                        />
                      </td>
                      <td>
                        <strong
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: piece.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                            color: piece.material_base === 'LATON' ? '#78350f' : '#1f2937',
                          }}
                        >
                          {piece.cod_pieza}
                        </strong>
                      </td>
                      <td>{piece.nombre_pieza}</td>
                      <td style={{ textAlign: 'center' }}>
                        <strong style={{ color: getStockColor(piece.current_stock, piece.min_stock) }}>
                          {piece.current_stock}
                        </strong>
                        {shouldShowAlert(piece.current_stock, piece.min_stock) && (
                          <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock crítico: menos del 50% del mínimo">
                            ⚠️
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                        {piece.material_base === 'PLATA' ? (
                          <div>
                            <div>
                              <strong>Acabado Plata:</strong>{' '}
                              <span style={{ color: getStockColor(piece.stock_destino_plata || 0, piece.min_stock) }}>
                                {piece.stock_destino_plata || 0}
                              </span>
                              {shouldShowAlert(piece.stock_destino_plata || 0, piece.min_stock) && (
                                <span style={{ marginLeft: '0.25rem', fontSize: '0.875rem' }} title="Stock crítico: menos del 50% del mínimo">
                                  ⚠️
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div>
                              <strong>Listo PVD:</strong>{' '}
                              <span style={{ color: getStockColor(piece.stock_destino_pvd || 0, piece.min_stock) }}>
                                {piece.stock_destino_pvd || 0}
                              </span>
                              {shouldShowAlert(piece.stock_destino_pvd || 0, piece.min_stock) && (
                                <span style={{ marginLeft: '0.25rem', fontSize: '0.875rem' }} title="Stock crítico: menos del 50% del mínimo">
                                  ⚠️
                                </span>
                              )}
                            </div>
                            <div>
                              <strong>Listo DLC:</strong>{' '}
                              <span style={{ color: getStockColor(piece.stock_destino_dlc || 0, piece.min_stock) }}>
                                {piece.stock_destino_dlc || 0}
                              </span>
                              {shouldShowAlert(piece.stock_destino_dlc || 0, piece.min_stock) && (
                                <span style={{ marginLeft: '0.25rem', fontSize: '0.875rem' }} title="Stock crítico: menos del 50% del mínimo">
                                  ⚠️
                                </span>
                              )}
                            </div>
                            <div style={{ marginTop: '0.25rem', paddingTop: '0.25rem', borderTop: '1px solid var(--border-color)' }}>
                              <small>
                                <strong>Acabado PVD:</strong>{' '}
                                <span style={{ color: getStockColor(piece.stock_destino_acabado_pvd || 0, piece.min_stock) }}>
                                  {piece.stock_destino_acabado_pvd || 0}
                                </span>
                                {shouldShowAlert(piece.stock_destino_acabado_pvd || 0, piece.min_stock) && (
                                  <span style={{ marginLeft: '0.25rem' }} title="Stock crítico: menos del 50% del mínimo">
                                    ⚠️
                                  </span>
                                )}
                              </small>
                            </div>
                            <div>
                              <small>
                                <strong>Acabado DLC:</strong>{' '}
                                <span style={{ color: getStockColor(piece.stock_destino_acabado_dlc || 0, piece.min_stock) }}>
                                  {piece.stock_destino_acabado_dlc || 0}
                                </span>
                                {shouldShowAlert(piece.stock_destino_acabado_dlc || 0, piece.min_stock) && (
                                  <span style={{ marginLeft: '0.25rem' }} title="Stock crítico: menos del 50% del mínimo">
                                    ⚠️
                                  </span>
                                )}
                              </small>
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>{piece.min_stock}</td>
                      <td style={{ textAlign: 'center', color: 'var(--accent)', fontWeight: 'bold' }}>
                        {piece.pending_of || 0}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {hasNeed ? (
                          <strong style={{ color: 'var(--error)' }}>{piece.needed}</strong>
                        ) : null}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          max={piece.current_stock}
                          className="input"
                          value={isSelected ? qty.toString() : ''}
                          onChange={(e) => {
                            const newValue = parseInt(e.target.value, 10) || 0;
                            if (newValue > piece.current_stock) {
                              handleUpdateQty(piece.id, piece.current_stock);
                            } else if (newValue < 1) {
                              handleUpdateQty(piece.id, 1);
                            } else {
                              handleUpdateQty(piece.id, newValue);
                            }
                          }}
                          disabled={!isSelected}
                          placeholder={
                            isSelected
                              ? ''
                              : maxQty > 0
                                ? Math.min(piece.needed || maxQty, maxQty).toString()
                                : '0'
                          }
                          title={`Disponible en taller: ${piece.current_stock}`}
                          style={{
                            width: '120px',
                            minWidth: '120px',
                            borderColor: isSelected && (qty > piece.current_stock || qty < 1)
                              ? 'var(--error)'
                              : undefined
                          }}
                        />
                        <small style={{
                          color: isSelected && (qty > piece.current_stock || qty < 1)
                            ? 'var(--error)'
                            : 'var(--text-secondary)'
                        }}>
                          Máx: {piece.current_stock}
                          {isSelected && qty > piece.current_stock && (
                            <span style={{ display: 'block', marginTop: '0.25rem' }}>
                              ⚠️ Excede el stock disponible
                            </span>
                          )}
                          {isSelected && qty < 1 && (
                            <span style={{ display: 'block', marginTop: '0.25rem' }}>
                              ⚠️ Mínimo: 1
                            </span>
                          )}
                        </small>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selectedLines.size > 0 && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <strong>Total seleccionado: {selectedLines.size} línea(s)</strong>
            <br />
            <small style={{ color: 'var(--text-secondary)' }}>
              {Array.from(selectedLines.values()).reduce((sum, qty) => sum + qty, 0)} unidades
            </small>
          </div>
        )}
      </Modal>

      {/* Modal Detalle OF */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedWorkOrder(null);
        }}
        title={selectedWorkOrder ? `OF: ${selectedWorkOrder.code}` : 'Detalle de OF'}
        size="large"
        footer={
          <div className="flex gap-2" style={{ justifyContent: 'flex-end', width: '100%' }}>
            {selectedWorkOrder &&
              selectedWorkOrder.status !== 'DONE' &&
              detailSelectedLines.size > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={handleCompleteSelectedLines}
                  disabled={isCompletingSelected || isCompletingAll}
                >
                  {isCompletingSelected ? 'Completando...' : 'Completar seleccionados'}
                </button>
              )}
            {selectedWorkOrder &&
              selectedWorkOrder.status !== 'DONE' &&
              selectedWorkOrder.lines.some((line) => line.qty_planned > line.qty_done) && (
                <button
                  className="btn btn-success"
                  onClick={() => handleCompleteAllLines(selectedWorkOrder)}
                  disabled={isCompletingAll || isCompletingSelected}
                >
                  {isCompletingAll ? 'Completando...' : 'Completar todo'}
                </button>
              )}
            {selectedWorkOrder &&
              selectedWorkOrder.status !== 'DONE' && (
                <button
                  className="btn btn-error"
                  style={{ backgroundColor: 'var(--error)', color: 'white' }}
                  onClick={() => {
                    handleCloseWorkOrder(selectedWorkOrder.id);
                    setIsDetailModalOpen(false);
                  }}
                >
                  Cerrar Orden
                </button>
              )}
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsDetailModalOpen(false);
                setSelectedWorkOrder(null);
                setDetailSelectedLines(new Set());
              }}
            >
              Cerrar
            </button>
          </div>
        }
      >
        {selectedWorkOrder && (() => {
          const pendingLineIds = selectedWorkOrder.lines
            .filter((line) => line.qty_planned > line.qty_done)
            .map((line) => line.id);
          const allDetailLinesSelected =
            pendingLineIds.length > 0 && pendingLineIds.every((id) => detailSelectedLines.has(id));
          return (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <p>
                  <strong>Estado:</strong>{' '}
                  <span style={{ color: getStatusColor(selectedWorkOrder.status) }}>
                    {getStatusLabel(selectedWorkOrder.status)}
                  </span>
                </p>
                <p>
                  <strong>Fecha Creación:</strong>{' '}
                  {formatDateTime(selectedWorkOrder.created_at)}
                </p>
                {selectedWorkOrder.completed_at && (
                  <p>
                    <strong>Fecha Completación:</strong>{' '}
                    {formatDateTime(selectedWorkOrder.completed_at)}
                  </p>
                )}
              </div>

              <h3 style={{ marginBottom: '1rem' }}>Líneas</h3>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={allDetailLinesSelected}
                          disabled={pendingLineIds.length === 0}
                          onChange={toggleSelectAllDetailLines}
                        />
                      </th>
                      <th>Pieza</th>
                      <th>Material</th>
                      <th>Planificado</th>
                      <th>Completado</th>
                      <th>Pendiente</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedWorkOrder.lines.map((line) => {
                      const pending = line.qty_planned - line.qty_done;
                      const qtyInput = lineQtyInputs.get(line.id) || '';
                      return (
                        <tr key={line.id}>
                          <td>
                            {pending > 0 ? (
                              <input
                                type="checkbox"
                                checked={detailSelectedLines.has(line.id)}
                                onChange={() => toggleDetailLineSelection(line.id)}
                              />
                            ) : null}
                          </td>
                          <td>
                            {line.metal_piece.cod_pieza}
                            <br />
                            <small style={{ color: 'var(--text-secondary)' }}>
                              {line.metal_piece.nombre_pieza}
                            </small>
                          </td>
                          <td>{line.metal_piece.material_base}</td>
                          <td>{line.qty_planned}</td>
                          <td>
                            <strong style={{ color: 'var(--success)' }}>{line.qty_done}</strong>
                          </td>
                          <td>
                            <strong style={{ color: pending > 0 ? 'var(--warning)' : 'var(--success)' }}>
                              {pending}
                            </strong>
                          </td>
                          <td>
                            {pending > 0 && selectedWorkOrder.status !== 'DONE' ? (
                              <div className="flex gap-1" style={{ alignItems: 'center' }}>
                                <input
                                  type="number"
                                  min="1"
                                  className="input"
                                  value={qtyInput}
                                  onChange={(e) => {
                                    const newMap = new Map(lineQtyInputs);
                                    newMap.set(line.id, e.target.value);
                                    setLineQtyInputs(newMap);
                                  }}
                                  placeholder="Cantidad"
                                  style={{ width: '80px' }}
                                />
                                <button
                                  className="btn btn-primary"
                                  onClick={() => {
                                    const qty = parseInt(qtyInput, 10);
                                    if (qty > 0) {
                                      handleCompleteLine(selectedWorkOrder.id, line.id, qty);
                                      const newMap = new Map(lineQtyInputs);
                                      newMap.delete(line.id);
                                      setLineQtyInputs(newMap);
                                    }
                                  }}
                                  style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                >
                                  Completar
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-secondary)' }}>Completada</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Sección de Comentarios */}
        {selectedWorkOrder && (
          <div style={{ marginTop: '2rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
            <CommentsSection
              entityType="WORK_ORDER"
              entityId={selectedWorkOrder.id}
            />
          </div>
        )}
      </Modal>

      <StockChangeModal
        isOpen={isStockChangeModalOpen}
        onClose={() => setIsStockChangeModalOpen(false)}
        title="Cambios de Stock - Orden de Fabricación Completada"
        changes={stockChanges}
      />
    </div>
  );
}

